import {
    CookieProvider,
    DarkModeProvider,
    DebugMenu,
    UnauthenticatedClientProvider,
    UnauthenticatedClientProviderContext,
} from '@amzn/event-engine-js-utils';
import AuthProvider from 'contexts/AuthProvider';
import EventEngineClientFactory from 'contexts/EventEngineClientFactory';
import { createBrowserHistory } from 'history';
import React, { FC, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Router } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import AppSettings from 'services/app-settings';

type UnauthenticatedClientProviderContextType = React.ContextType<
    typeof UnauthenticatedClientProviderContext
>;

type RequiredUnauthenticatedClientProviderContextType = {
    [K in keyof UnauthenticatedClientProviderContextType]-?: NonNullable<
        UnauthenticatedClientProviderContextType[K]
    >;
};

const history = createBrowserHistory();

const UnauthenticatedAppProviders: FC = ({ children }) => {
    const [
        unauthenticatedAppProps,
        setUnauthenticatedAppProps,
    ] = useState<RequiredUnauthenticatedClientProviderContextType>();

    useEffect(() => {
        (async () => {
            const unauthenticatedAwsClient = EventEngineClientFactory.getClientSingleton(
                'UnauthenticatedAWSClient'
            );
            const unauthenticatedClients = await unauthenticatedAwsClient.getClient();
            setUnauthenticatedAppProps(unauthenticatedClients);
        })();
    }, []);

    return (
        <UnauthenticatedClientProvider {...unauthenticatedAppProps!}>
            {children!}
        </UnauthenticatedClientProvider>
    );
};

const Providers: FC = ({ children }) => {
    const [isSettingsHydrated, setIsSettingsHydrated] = useState(false);

    useEffect(() => {
        AppSettings().then(() => {
            setIsSettingsHydrated(true);
        });
    }, []);

    if (!isSettingsHydrated) {
        return null;
    }

    return (
        <RecoilRoot>
            <CookieProvider>
                <DarkModeProvider>
                    <DebugMenu
                        gitInformation={{
                            sha: process.env.REACT_APP_GIT_SHA,
                            branch: process.env.REACT_APP_GIT_BRANCH,
                        }}>
                        <Router history={history}>
                            <UnauthenticatedAppProviders>
                                <AuthProvider>
                                    <HelmetProvider>{children}</HelmetProvider>
                                </AuthProvider>
                            </UnauthenticatedAppProviders>
                        </Router>
                    </DebugMenu>
                </DarkModeProvider>
            </CookieProvider>
        </RecoilRoot>
    );
};

export default Providers;
